/** Auto-generated route registry — Phase 1G / generate-screens */
import { lazy, Suspense, type ReactElement } from 'react';
import { useRoutes } from 'react-router-dom';
import { AuthLayout } from '@beatq/ui';
import { NotFoundFallback } from '@beatq/shared';
import { BusinessShell } from '../shell/BusinessShell.js';
import { AccountScopeLayout } from '../layouts/AccountScopeLayout.js';
import { LandingRedirect } from '../layouts/LandingRedirect.js';
import { PathQueryBridge } from '../layouts/PathQueryBridge.js';
import { SignInReturnGate } from '../layouts/SignInReturnGate.js';
import {
  APPROVAL_ID_STORAGE_KEY,
  POLICY_ID_STORAGE_KEY,
} from '../features/policies-approvals/catalog.js';
import { INVOICE_ID_STORAGE_KEY } from '../features/invoices-reports/catalog.js';
import { DEPARTMENT_ID_STORAGE_KEY } from '../features/org-structure/catalog.js';
import { EMPLOYEE_ID_STORAGE_KEY } from '../features/employees/catalog.js';

const CorporateSignInPage = lazy(() =>
  import('../features/signup-setup/CorporateSignInPage.js').then((m) => ({
    default: m.CorporateSignInPage,
  })),
);
const AcceptInvitePage = lazy(() =>
  import('../features/employees/EmployeesPages.js').then((m) => ({
    default: m.AcceptInvitePage,
  })),
);
const SCRBIZ001Page = lazy(() => import('../pages/SCR-BIZ-001.js'));
const SCRBIZ002Page = lazy(() => import('../pages/SCR-BIZ-002.js'));
const SCRBIZ003Page = lazy(() => import('../pages/SCR-BIZ-003.js'));
const SCRBIZ004Page = lazy(() => import('../pages/SCR-BIZ-004.js'));
const SCRBIZ005Page = lazy(() => import('../pages/SCR-BIZ-005.js'));
const SCRBIZ006Page = lazy(() => import('../pages/SCR-BIZ-006.js'));
const SCRBIZ007Page = lazy(() => import('../pages/SCR-BIZ-007.js'));
const SCRBIZ008Page = lazy(() => import('../pages/SCR-BIZ-008.js'));
const SCRBIZ009Page = lazy(() => import('../pages/SCR-BIZ-009.js'));
const SCRBIZ010Page = lazy(() => import('../pages/SCR-BIZ-010.js'));
const SCRBIZ011Page = lazy(() => import('../pages/SCR-BIZ-011.js'));
const SCRBIZ012Page = lazy(() => import('../pages/SCR-BIZ-012.js'));
const SCRBIZ013Page = lazy(() => import('../pages/SCR-BIZ-013.js'));
const SCRBIZ014Page = lazy(() => import('../pages/SCR-BIZ-014.js'));
const SCRBIZ015Page = lazy(() => import('../pages/SCR-BIZ-015.js'));
const SCRBIZ016Page = lazy(() => import('../pages/SCR-BIZ-016.js'));
const SCRBIZ017Page = lazy(() => import('../pages/SCR-BIZ-017.js'));
const SCRBIZ018Page = lazy(() => import('../pages/SCR-BIZ-018.js'));
const SCRBIZ019Page = lazy(() => import('../pages/SCR-BIZ-019.js'));
const SCRBIZ020Page = lazy(() => import('../pages/SCR-BIZ-020.js'));
const SCRBIZ021Page = lazy(() => import('../pages/SCR-BIZ-021.js'));
const SCRBIZ022Page = lazy(() => import('../pages/SCR-BIZ-022.js'));
const SCRBIZ023Page = lazy(() => import('../pages/SCR-BIZ-023.js'));
const SCRBIZ024Page = lazy(() => import('../pages/SCR-BIZ-024.js'));
const SCRBIZ025Page = lazy(() => import('../pages/SCR-BIZ-025.js'));
const SCRBIZ026Page = lazy(() => import('../pages/SCR-BIZ-026.js'));
const SCRBIZ027Page = lazy(() => import('../pages/SCR-BIZ-027.js'));
const SCRBIZ028Page = lazy(() => import('../pages/SCR-BIZ-028.js'));
const SCRBIZ029Page = lazy(() => import('../pages/SCR-BIZ-029.js'));
const SCRBIZ030Page = lazy(() => import('../pages/SCR-BIZ-030.js'));
const SCRBIZ031Page = lazy(() => import('../pages/SCR-BIZ-031.js'));

export type LayoutShell = 'public' | 'auth' | 'account' | 'floor';

export interface ScreenRoute {
  screenId: string;
  path: string;
  layoutShell: LayoutShell;
  theme: 'standard' | 'floor' | 'dense';
  element: ReactElement;
}

export const screenRoutes: ScreenRoute[] = [
  {
    path: '/corporate-sign-up',
    screenId: 'SCR-BIZ-001',
    layoutShell: 'auth',
    theme: 'standard',
    element: <SCRBIZ001Page />,
  },
  {
    path: '/account-verification',
    screenId: 'SCR-BIZ-002',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ002Page />,
  },
  {
    path: '/setup-checklist',
    screenId: 'SCR-BIZ-003',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ003Page />,
  },
  {
    path: '/account-overview',
    screenId: 'SCR-BIZ-004',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ004Page />,
  },
  {
    path: '/department-list',
    screenId: 'SCR-BIZ-005',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ005Page />,
  },
  {
    path: '/department-editor',
    screenId: 'SCR-BIZ-006',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ006Page />,
  },
  {
    path: '/department-editor/:id',
    screenId: 'SCR-BIZ-006',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ006Page />,
  },
  {
    path: '/cost-centre-list',
    screenId: 'SCR-BIZ-007',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ007Page />,
  },
  {
    path: '/hierarchy-view',
    screenId: 'SCR-BIZ-008',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ008Page />,
  },
  {
    path: '/employee-list',
    screenId: 'SCR-BIZ-009',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ009Page />,
  },
  {
    path: '/invite-employees',
    screenId: 'SCR-BIZ-010',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ010Page />,
  },
  {
    path: '/employee-detail/:id',
    screenId: 'SCR-BIZ-011',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ011Page />,
  },
  {
    path: '/employee-detail',
    screenId: 'SCR-BIZ-011',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ011Page />,
  },
  {
    path: '/bulk-employee-import',
    screenId: 'SCR-BIZ-012',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ012Page />,
  },
  {
    path: '/policy-list',
    screenId: 'SCR-BIZ-013',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ013Page />,
  },
  {
    path: '/policy-editor',
    screenId: 'SCR-BIZ-014',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ014Page />,
  },
  {
    path: '/policy-editor/:policyId',
    screenId: 'SCR-BIZ-014',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ014Page />,
  },
  {
    path: '/policy-simulator',
    screenId: 'SCR-BIZ-015',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ015Page />,
  },
  {
    path: '/policy-simulator/:policyId',
    screenId: 'SCR-BIZ-015',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ015Page />,
  },
  {
    path: '/approval-chain',
    screenId: 'SCR-BIZ-016',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ016Page />,
  },
  {
    path: '/delegation-settings',
    screenId: 'SCR-BIZ-017',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ017Page />,
  },
  {
    path: '/approval-queue',
    screenId: 'SCR-BIZ-018',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ018Page />,
  },
  {
    path: '/approval-detail',
    screenId: 'SCR-BIZ-019',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ019Page />,
  },
  {
    path: '/approval-detail/:id',
    screenId: 'SCR-BIZ-019',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ019Page />,
  },
  {
    path: '/approvals/:id',
    screenId: 'SCR-BIZ-019',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ019Page />,
  },
  {
    path: '/budget-monitor',
    screenId: 'SCR-BIZ-020',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ020Page />,
  },
  {
    path: '/budget-monitor/:budgetId',
    screenId: 'SCR-BIZ-020',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ020Page />,
  },
  {
    path: '/budget-alerts',
    screenId: 'SCR-BIZ-021',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ021Page />,
  },
  {
    path: '/budget-alerts/:budgetId',
    screenId: 'SCR-BIZ-021',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ021Page />,
  },
  {
    path: '/invoice-list',
    screenId: 'SCR-BIZ-022',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ022Page />,
  },
  {
    path: '/invoices/:id',
    screenId: 'SCR-BIZ-023',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ023Page />,
  },
  {
    path: '/invoice-detail',
    screenId: 'SCR-BIZ-023',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ023Page />,
  },
  {
    path: '/invoices/:id/query',
    screenId: 'SCR-BIZ-024',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ024Page />,
  },
  {
    path: '/invoice-query',
    screenId: 'SCR-BIZ-024',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ024Page />,
  },
  {
    path: '/invoices/:id/payment',
    screenId: 'SCR-BIZ-025',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ025Page />,
  },
  {
    path: '/payment-record',
    screenId: 'SCR-BIZ-025',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ025Page />,
  },
  {
    path: '/spend-report',
    screenId: 'SCR-BIZ-026',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ026Page />,
  },
  {
    path: '/export-centre',
    screenId: 'SCR-BIZ-027',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ027Page />,
  },
  {
    path: '/account-settings',
    screenId: 'SCR-BIZ-028',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ028Page />,
  },
  {
    path: '/corporate-api-clients',
    screenId: 'SCR-BIZ-029',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ029Page />,
  },
  {
    path: '/account-activity',
    screenId: 'SCR-BIZ-030',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ030Page />,
  },
  {
    path: '/alerts-panel',
    screenId: 'SCR-BIZ-031',
    layoutShell: 'public',
    theme: 'standard',
    element: <SCRBIZ031Page />,
  },
];

const routeTree = [
  {
    element: <BusinessShell />,
    children: [
      { path: '/', element: <LandingRedirect /> },
      { path: '/account-verification', screenId: 'SCR-BIZ-002', element: <SCRBIZ002Page /> },
      { path: '/setup-checklist', screenId: 'SCR-BIZ-003', element: <SCRBIZ003Page /> },
      { path: '/account-overview', screenId: 'SCR-BIZ-004', element: <SCRBIZ004Page /> },
      { path: '/department-list', screenId: 'SCR-BIZ-005', element: <SCRBIZ005Page /> },
      { path: '/department-editor', screenId: 'SCR-BIZ-006', element: <SCRBIZ006Page /> },
      {
        path: '/department-editor/:id',
        screenId: 'SCR-BIZ-006',
        element: (
          <PathQueryBridge
            pathKey="id"
            queryKey="departmentId"
            storageKey={DEPARTMENT_ID_STORAGE_KEY}
          >
            <SCRBIZ006Page />
          </PathQueryBridge>
        ),
      },
      { path: '/cost-centre-list', screenId: 'SCR-BIZ-007', element: <SCRBIZ007Page /> },
      { path: '/hierarchy-view', screenId: 'SCR-BIZ-008', element: <SCRBIZ008Page /> },
      { path: '/employee-list', screenId: 'SCR-BIZ-009', element: <SCRBIZ009Page /> },
      { path: '/invite-employees', screenId: 'SCR-BIZ-010', element: <SCRBIZ010Page /> },
      {
        path: '/employee-detail/:id',
        screenId: 'SCR-BIZ-011',
        element: (
          <PathQueryBridge pathKey="id" queryKey="employeeId" storageKey={EMPLOYEE_ID_STORAGE_KEY}>
            <SCRBIZ011Page />
          </PathQueryBridge>
        ),
      },
      { path: '/employee-detail', screenId: 'SCR-BIZ-011', element: <SCRBIZ011Page /> },
      { path: '/bulk-employee-import', screenId: 'SCR-BIZ-012', element: <SCRBIZ012Page /> },
      { path: '/policy-list', screenId: 'SCR-BIZ-013', element: <SCRBIZ013Page /> },
      { path: '/policy-editor', screenId: 'SCR-BIZ-014', element: <SCRBIZ014Page /> },
      {
        path: '/policy-editor/:policyId',
        screenId: 'SCR-BIZ-014',
        element: (
          <PathQueryBridge
            pathKey="policyId"
            queryKey="policyId"
            storageKey={POLICY_ID_STORAGE_KEY}
          >
            <SCRBIZ014Page />
          </PathQueryBridge>
        ),
      },
      { path: '/policy-simulator', screenId: 'SCR-BIZ-015', element: <SCRBIZ015Page /> },
      {
        path: '/policy-simulator/:policyId',
        screenId: 'SCR-BIZ-015',
        element: (
          <PathQueryBridge
            pathKey="policyId"
            queryKey="policyId"
            storageKey={POLICY_ID_STORAGE_KEY}
          >
            <SCRBIZ015Page />
          </PathQueryBridge>
        ),
      },
      { path: '/approval-chain', screenId: 'SCR-BIZ-016', element: <SCRBIZ016Page /> },
      { path: '/delegation-settings', screenId: 'SCR-BIZ-017', element: <SCRBIZ017Page /> },
      { path: '/approval-queue', screenId: 'SCR-BIZ-018', element: <SCRBIZ018Page /> },
      { path: '/approval-detail', screenId: 'SCR-BIZ-019', element: <SCRBIZ019Page /> },
      {
        path: '/approval-detail/:id',
        screenId: 'SCR-BIZ-019',
        element: (
          <PathQueryBridge pathKey="id" queryKey="approvalId" storageKey={APPROVAL_ID_STORAGE_KEY}>
            <SCRBIZ019Page />
          </PathQueryBridge>
        ),
      },
      {
        path: '/approvals/:id',
        screenId: 'SCR-BIZ-019',
        element: (
          <PathQueryBridge pathKey="id" queryKey="approvalId" storageKey={APPROVAL_ID_STORAGE_KEY}>
            <SCRBIZ019Page />
          </PathQueryBridge>
        ),
      },
      { path: '/budget-monitor', screenId: 'SCR-BIZ-020', element: <SCRBIZ020Page /> },
      { path: '/budget-monitor/:budgetId', element: <SCRBIZ020Page /> },
      { path: '/budget-alerts', screenId: 'SCR-BIZ-021', element: <SCRBIZ021Page /> },
      { path: '/budget-alerts/:budgetId', element: <SCRBIZ021Page /> },
      { path: '/invoice-list', screenId: 'SCR-BIZ-022', element: <SCRBIZ022Page /> },
      { path: '/invoice-detail', screenId: 'SCR-BIZ-023', element: <SCRBIZ023Page /> },
      {
        path: '/invoices/:id/query',
        screenId: 'SCR-BIZ-024',
        element: (
          <PathQueryBridge pathKey="id" queryKey="invoiceId" storageKey={INVOICE_ID_STORAGE_KEY}>
            <SCRBIZ024Page />
          </PathQueryBridge>
        ),
      },
      {
        path: '/invoices/:id/payment',
        screenId: 'SCR-BIZ-025',
        element: (
          <PathQueryBridge pathKey="id" queryKey="invoiceId" storageKey={INVOICE_ID_STORAGE_KEY}>
            <SCRBIZ025Page />
          </PathQueryBridge>
        ),
      },
      {
        path: '/invoices/:id',
        element: (
          <PathQueryBridge pathKey="id" queryKey="invoiceId" storageKey={INVOICE_ID_STORAGE_KEY}>
            <SCRBIZ023Page />
          </PathQueryBridge>
        ),
      },
      { path: '/invoice-query', screenId: 'SCR-BIZ-024', element: <SCRBIZ024Page /> },
      { path: '/payment-record', screenId: 'SCR-BIZ-025', element: <SCRBIZ025Page /> },
      { path: '/spend-report', screenId: 'SCR-BIZ-026', element: <SCRBIZ026Page /> },
      { path: '/export-centre', screenId: 'SCR-BIZ-027', element: <SCRBIZ027Page /> },
      { path: '/account-settings', screenId: 'SCR-BIZ-028', element: <SCRBIZ028Page /> },
      { path: '/corporate-api-clients', screenId: 'SCR-BIZ-029', element: <SCRBIZ029Page /> },
      { path: '/account-activity', screenId: 'SCR-BIZ-030', element: <SCRBIZ030Page /> },
      { path: '/alerts-panel', screenId: 'SCR-BIZ-031', element: <SCRBIZ031Page /> },
      { path: '/a/:accountSlug/*', element: <AccountScopeLayout /> },
    ],
  },
  {
    element: <AuthLayout surface="business" />,
    children: [
      {
        path: '/corporate-sign-in',
        screenId: 'AUTH-SIGN-IN',
        element: (
          <SignInReturnGate>
            <CorporateSignInPage />
          </SignInReturnGate>
        ),
      },
      { path: '/corporate-sign-up', screenId: 'SCR-BIZ-001', element: <SCRBIZ001Page /> },
      {
        path: '/accept-invite',
        screenId: 'AUTH-ACCEPT-INVITE',
        element: <AcceptInvitePage />,
      },
    ],
  },
  { path: '*', element: <NotFoundFallback app="business-portal" /> },
];

export function AppRoutes() {
  return (
    <Suspense fallback={<p role="status">Loading…</p>}>
      <RoutesRenderer />
    </Suspense>
  );
}

function RoutesRenderer() {
  return useRoutes(routeTree);
}
